import { ResponsiveContainer, RadialBarChart, RadialBar, PolarAngleAxis } from "recharts";
import { Home, Trees, School, HandHeart, Info } from "lucide-react";

import { useDashboard } from "@/lib/dataContext";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table";
import { fmt, fmtPct, chartColor } from "@/lib/format";

const GapGauge = ({ label, covered, coveredPct, gapCount, color }) => {
    const chartData = [{ name: label, value: coveredPct, fill: color }];
    return (
        <Card className="border-border/70 bg-card p-5 shadow-card">
            <p className="text-sm font-medium text-foreground">{label}</p>
            <div className="relative mx-auto mt-2 h-[170px] w-[170px]">
                <ResponsiveContainer width="100%" height="100%">
                    <RadialBarChart innerRadius="72%" outerRadius="100%" data={chartData} startAngle={90} endAngle={-270}>
                        <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                        <RadialBar dataKey="value" cornerRadius={12} background={{ fill: "hsl(var(--muted))" }} />
                    </RadialBarChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="font-display text-3xl font-semibold tabular text-foreground">{fmtPct(coveredPct, 0)}</span>
                    <span className="text-xs text-muted-foreground">covered</span>
                </div>
            </div>
            <p className="mt-2 text-center text-xs text-muted-foreground">{fmt(covered)} samithis covered &middot; {fmt(gapCount)} still to go</p>
        </Card>
    );
};

const show = (value) => value === null || value === undefined ? "—" : fmt(value);

export default function Outreach() {
    const { data } = useDashboard();
    const so = data.service_and_outreach.state_summary;
    const districtOutreach = data.service_and_outreach.district_outreach;
    const rows = districtOutreach?.district_requirements || [];
    const es = data.executive_summary;
    const totalSamithis = es.samithis;

    const villageGap = so.samithis_without_adopted_village;
    const schoolGap = so.samithis_without_vidya_jyothi_school;
    const villageCoveredPct = 100 - villageGap.share_pct;
    const schoolCoveredPct = 100 - schoolGap.share_pct;

    return (
        <div className="space-y-8">
            <PageHeader eyebrow="Service & Outreach" title="Reaching the community" description="The organisation's grassroots service footprint across villages and schools." />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <KpiCard index={0} label="Service Beneficiaries" value={fmt(es.service_beneficiaries_full_year)} sub="People served this year" icon={HandHeart} tone="success" />
                <KpiCard index={1} label="Adopted Villages" value={fmt(so.adopted_villages)} sub="Under sustained seva" icon={Home} tone="primary" />
                <KpiCard index={2} label="Tribal Villages" value={fmt(so.tribal_villages)} sub="Of adopted villages" icon={Trees} tone="accent" />
                <KpiCard index={3} label="Vidya Jyothi Schools" value={fmt(so.vidya_jyothi_schools)} sub="Value education centres" icon={School} tone="warning" />
            </div>

            <div>
                <h2 className="mb-4 font-display text-xl font-semibold text-foreground">Coverage & Opportunity</h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <GapGauge label="Samithis with an adopted village" covered={totalSamithis - villageGap.count} coveredPct={villageCoveredPct} gapCount={villageGap.count} color={chartColor(3)} />
                    <GapGauge label="Samithis with a Vidya Jyothi school" covered={totalSamithis - schoolGap.count} coveredPct={schoolCoveredPct} gapCount={schoolGap.count} color={chartColor(1)} />
                </div>
            </div>

            <div>
                <div className="mb-4">
                    <h2 className="font-display text-xl font-semibold text-foreground">District-level Outreach Requirements</h2>
                    <p className="mt-1 text-sm text-muted-foreground">Minimum requirement: one adopted village and one Vidya Jyothi school for every three organisational units (Samithi + Bhajan Mandali).</p>
                </div>
                <Card className="overflow-hidden border-border/70 bg-card shadow-card">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="hover:bg-transparent">
                                    <TableCell className="font-semibold">District</TableCell>
                                    <TableCell className="text-right font-semibold">Samithis</TableCell>
                                    <TableCell className="text-right font-semibold">Bhajan Mandalis</TableCell>
                                    <TableCell className="text-right font-semibold">Total Units</TableCell>
                                    <TableCell className="text-right font-semibold">Required Villages</TableCell>
                                    <TableCell className="text-right font-semibold">Actual Villages</TableCell>
                                    <TableCell className="text-right font-semibold">Village Gap</TableCell>
                                    <TableCell className="text-right font-semibold">Required VJ Schools</TableCell>
                                    <TableCell className="text-right font-semibold">Actual VJ Schools</TableCell>
                                    <TableCell className="text-right font-semibold">VJ Gap</TableCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((r) => (
                                    <TableRow key={r.district_id} className="border-border/50">
                                        <TableCell className="font-medium text-foreground">{r.district_name}</TableCell>
                                        <TableCell className="text-right tabular">{fmt(r.samithis)}</TableCell>
                                        <TableCell className="text-right tabular">{fmt(r.bhajan_mandalis)}</TableCell>
                                        <TableCell className="text-right tabular">{fmt(r.total_units)}</TableCell>
                                        <TableCell className="text-right tabular">{fmt(r.required_adopted_villages)}</TableCell>
                                        <TableCell className="text-right tabular">{show(r.actual_adopted_villages)}</TableCell>
                                        <TableCell className="text-right tabular">{show(r.adopted_village_gap)}</TableCell>
                                        <TableCell className="text-right tabular">{fmt(r.required_vidya_jyothi_schools)}</TableCell>
                                        <TableCell className="text-right tabular">{show(r.actual_vidya_jyothi_schools)}</TableCell>
                                        <TableCell className="text-right tabular">{show(r.vidya_jyothi_gap)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </Card>
                <Card className="mt-4 border-border/70 bg-secondary/40 p-4 shadow-card">
                    <div className="flex items-start gap-3"><Info className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" /><p className="text-sm leading-relaxed text-muted-foreground">District-wise required coverage is now shown. Actual district allocations remain “—” because the supplied Version 3 data contains only State-level outreach totals. Add district identifiers and actual village/school counts to calculate and rank the gaps automatically.</p></div>
                </Card>
            </div>

            <Card className="border-border/70 bg-gradient-subtle p-5 shadow-card">
                <div className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent"><Info className="h-6 w-6" /></span>
                    <div><h3 className="font-semibold text-foreground">Growth opportunity</h3><p className="mt-1 max-w-3xl text-sm leading-relaxed text-muted-foreground">While {fmt(so.adopted_villages)} villages and {fmt(so.vidya_jyothi_schools)} schools are served, {fmtPct(villageGap.share_pct)} of samithis are yet to adopt a village and {fmtPct(schoolGap.share_pct)} are yet to host a Vidya Jyothi school &mdash; a clear pathway to widen the organisation&apos;s outreach.</p></div>
                </div>
            </Card>
        </div>
    );
}
